import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

// Pill is the only button shape (brand guide). One primary per view.
// Primary uses --color-primary (a deeper Codemonk red) so white text passes WCAG AA at 16 px.
const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-pill font-heading font-semibold transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-fg hover:bg-primary-hover active:bg-primary-hover",
        secondary:
          "border-2 border-secondary bg-surface text-secondary hover:bg-secondary-subtle active:bg-secondary-muted",
        solid: "bg-secondary text-secondary-fg hover:bg-secondary-hover",
        danger: "bg-danger text-fg-inverse hover:bg-danger-fg",
        ghost: "bg-transparent text-heading hover:bg-secondary-subtle",
        link: "bg-transparent px-0 text-link underline underline-offset-4 hover:text-link-hover",
      },
      size: {
        default: "min-h-11 px-6 py-2 text-base",
        sm: "min-h-9 px-4 py-1 text-sm",
        lg: "min-h-12 px-8 py-3 text-lg",
        icon: "h-11 w-11 p-0",
      },
    },
    compoundVariants: [{ variant: "link", className: "min-h-0 px-0 py-0" }],
    defaultVariants: { variant: "primary", size: "default" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : (type ?? "button")}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
